import { useCallback, useEffect, useRef, useState } from "react";
import mapService from "services/mapService";
import { mapper, type IpPoint } from "../MapModel";
import { formatSchedule } from "../utils/defibSchedule";

const EMPTY_POINTS: IpPoint[] = [];
interface CategoryData {
  scope: string;
  points: IpPoint[];
  ready: boolean;
  loading: boolean;
  error: boolean;
}

export const useMapData = (categoryId: number, locale: string) => {
  // Complete categories remain cached for the lifetime of the map, by language.
  // Camera movement changes drawing only; it never invalidates these results.
  const cache = useRef(new Map<string, IpPoint[]>());
  const request = useRef(0);
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  const scope = `${locale}:${categoryId}`;
  const enabled = categoryId !== 11;
  const [data, setData] = useState<CategoryData>({ scope: "", points: EMPTY_POINTS, ready: false, loading: false, error: false });

  useEffect(() => {
    const generation = ++request.current;
    if (!enabled) return;
    const cached = cache.current.get(scope);
    if (cached) {
      setData({ scope, points: cached, ready: true, loading: false, error: false });
      return;
    }
    const controller = new AbortController();
    setData({ scope, points: EMPTY_POINTS, ready: false, loading: true, error: false });
    const load = async (): Promise<IpPoint[]> => {
      if (categoryId === 10) {
        const response: any = await mapService.getDefibrilators(controller.signal, locale);
        return [...response.On, ...response.On112, ...response.Off, ...response.Off112].map((entry) => ({
          ...entry, id: String(entry.id), icon: mapper[10].icon,
          latitudine: Number(entry.latitudine), longitudine: Number(entry.longitudine),
          numar_telefon: entry.numar_telefon || entry.defibrilator.phone,
          canCallEmergency: entry.defibrilator.canCallEmergency,
          defibrilator: { ...entry.defibrilator, defibrilator_schedules_id: formatSchedule(entry.defibrilator.defibrilator_schedules_id) },
        }));
      }
      const response = await mapService.getIpPoints(categoryId, locale, controller.signal);
      return response.data.map((entry) => ({
        ...entry.attributes, id: String(entry.id), icon: mapper[categoryId]?.icon,
        latitudine: Number(entry.attributes.latitudine), longitudine: Number(entry.attributes.longitudine),
      } as IpPoint));
    };
    load().then((points) => {
      if (controller.signal.aborted || generation !== request.current) return;
      cache.current.set(scope, points);
      setData({ scope, points, ready: true, loading: false, error: false });
    }).catch(() => {
      if (controller.signal.aborted || generation !== request.current) return;
      setData({ scope, points: EMPTY_POINTS, ready: false, loading: false, error: true });
    });
    return () => controller.abort();
  }, [categoryId, enabled, locale, revision, scope]);

  const matchesScope = enabled && data.scope === scope;
  return {
    points: matchesScope ? data.points : EMPTY_POINTS,
    ready: !enabled || (matchesScope && data.ready),
    loading: enabled && (!matchesScope || data.loading),
    error: matchesScope && data.error,
    retry,
  };
};
